import { useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Radio,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useVideoList } from '@/api/queries';
import type { AdminVideo, VideoPurpose } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ListCount, RowSkeletons } from '@/components/PagedList';
import { PageLoader, ErrorAlert } from '@/components/QueryState';
import { VideoStatusBadge } from '@/components/StatusBadge';
import { formatDuration } from '@/lib/format';
import { SEARCH_DEBOUNCE_MS } from '@/lib/useListUrlState';
import { LinkTestStreamForm } from './LinkTestStreamForm';
import { TEST_STREAMS_ENABLED, testStreamsUnavailable } from './testStreams';
import { UploadVideoForm } from './UploadVideoForm';

interface Props {
  opened: boolean;
  onClose: () => void;
  purpose: VideoPurpose;
  title: string;
  defaultVideoTitle?: string;
  /** Called with the chosen / newly created video. */
  onPick: (video: AdminVideo) => void | Promise<void>;
}

/** Attach a video: choose from the library, upload a new file, or (dev) link a test stream. */
export function VideoPickerModal({
  opened,
  onClose,
  purpose,
  title,
  defaultVideoTitle,
  onPick,
}: Props) {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS);
  // Keep the chosen video itself, so it stays chosen when a new search hides it.
  const [chosen, setChosen] = useState<AdminVideo | null>(null);
  const videos = useVideoList({ q: debounced || undefined, purpose }, { enabled: opened });
  const [busy, setBusy] = useState(false);

  const pick = async (v: AdminVideo) => {
    setBusy(true);
    try {
      await onPick(v);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Tabs defaultValue="library" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="library">From library</Tabs.Tab>
          <Tabs.Tab value="upload">Upload new</Tabs.Tab>
          {TEST_STREAMS_ENABLED && !testStreamsUnavailable() && (
            <Tabs.Tab value="static">Link test stream</Tabs.Tab>
          )}
        </Tabs.List>
        <Tabs.Panel value="library">
          <Stack gap="sm">
            <TextInput
              leftSection={<IconSearch size={16} />}
              placeholder="Search videos"
              aria-label="Search videos"
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
            />
            {videos.isPending ? (
              <PageLoader />
            ) : videos.error ? (
              <ErrorAlert error={videos.error} />
            ) : videos.data.items.length === 0 ? (
              <EmptyState title="No videos found">Upload one, or change the search.</EmptyState>
            ) : (
              <ScrollArea.Autosize
                mah={360}
                style={{ opacity: videos.isPlaceholderData ? 0.6 : 1 }}
              >
                <Radio.Group
                  value={chosen?.id ?? null}
                  onChange={(id) => setChosen(videos.data?.items.find((v) => v.id === id) ?? null)}
                  aria-label="Videos"
                >
                  <Stack gap={6}>
                    {videos.data.items.map((v) => (
                      <Radio
                        key={v.id}
                        value={v.id}
                        label={
                          <Group gap="xs" wrap="nowrap">
                            <Text size="sm" fw={500}>
                              {v.title}
                            </Text>
                            <VideoStatusBadge status={v.status} />
                            <Text size="xs" c="dimmed">
                              {formatDuration(v.duration_s)}
                            </Text>
                            {v.used_by.length > 0 && (
                              <Text size="xs" c="dimmed">
                                Used by {v.used_by.length}
                              </Text>
                            )}
                          </Group>
                        }
                      />
                    ))}
                  </Stack>
                </Radio.Group>
                {videos.isFetchingNextPage && <RowSkeletons height={24} />}
              </ScrollArea.Autosize>
            )}
            {videos.data && videos.data.items.length > 0 && (
              <Group justify="space-between">
                <ListCount
                  shown={videos.data.items.length}
                  total={videos.data.total}
                  noun="videos"
                />
                {videos.hasNextPage && (
                  <Button
                    variant="default"
                    size="xs"
                    loading={videos.isFetchingNextPage}
                    onClick={() => void videos.fetchNextPage()}
                  >
                    Load more
                  </Button>
                )}
              </Group>
            )}
            <Group justify="flex-end" wrap="nowrap">
              {chosen && (
                <Text size="sm" c="dimmed" mr="auto" truncate>
                  Chosen: {chosen.title}
                </Text>
              )}
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!chosen} loading={busy} onClick={() => chosen && void pick(chosen)}>
                Attach video
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="upload">
          <UploadVideoForm purpose={purpose} defaultTitle={defaultVideoTitle} onCreated={pick} />
        </Tabs.Panel>
        {TEST_STREAMS_ENABLED && (
          <Tabs.Panel value="static">
            <LinkTestStreamForm
              purpose={purpose}
              defaultTitle={defaultVideoTitle}
              onLinked={(v) => void pick(v)}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Modal>
  );
}
